import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  IconButton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  FormGroup,
} from '@mui/material';
import { ContainerForm } from 'components/shared/container_form';
import { Stack } from '@mui/system';
import { PageTitle } from 'components/shared/page_title';
import { Colors } from 'theme';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentComponent from 'components/shared/document_component';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { validateCPF } from 'services/utils/validate_cpf';
import { formatVisibleCPF } from 'services/utils/format_cpf';
import { mascaraTelefone } from 'utils/format_phone';
import BeneficiarioAPI from 'api/cifa/beneficiario.api';
import useCifaStore, { ICifaForm } from 'stores/cifa/cifa.store';
import CifaAPI from 'api/cifa/cifa.api';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';

// Tipos
type Beneficiary = {
  cifa_uuid: string;
  nome: string;
  cpf: string;
  contato_telefonico: string;
  unidade_familiar: string;
  comunidade_localidade: string;
  extensionista: {
    nome: string;
    perfil: string;
    registro: string;
  };
  atendimentos: {
    cifa_uuid: string;
    tipo_atendimento: string;
    atividades: string[];
  }[];
};

// Tipo auxiliar para o formulário
type BeneficiaryFormValues = {
  name: string;
  cpf: string;
  telefone: string;
  unidadeFamiliar: string;
  comunidade: string;
  tipoAtividade: string;
  atividades: string[];
  extensionistaNome: string;
  extensionistaPerfil: string;
  extensionistaRegistro: string;
};

export type Step1FormValues = {
  beneficiaries: string;
  ataUuid: string | null;
  parceria: string;
  beneficiariosParceria: string;
  docParceriaUuid: string | null;
  relatorioUuid: string | null;
  instituicoes: string[];
  outraInstituicao: string;
  relatorioUuid_empaer: string | null;
  relatorioUuid_ensino: string | null;
  relatorioUuid_ongs: string | null;
  relatorioUuid_privadas: string | null;
};

interface Step1Props {
  onNext: (values: Step1FormValues) => void;
  initialData?: Step1FormValues;
}

const INSTITUICOES_PARCEIRAS = [
  { label: 'EMPAER MT', value: 'empaer' },
  { label: 'Instituições de Ensino', value: 'ensino' },
  { label: 'Organizações – ONGs', value: 'ongs' },
  { label: 'Instituições privadas/outros', value: 'privadas' },
];

const ATIVIDADES_SEIAF = [
  { id: '1', nome: 'Plantio de milho' },
  { id: '2', nome: 'Criação de galinhas' },
  { id: '3', nome: 'Horta comunitária' },
];

const PERFIS = [
  { id: '1', nome: 'Engenheiro Agrônomo' },
  { id: '2', nome: 'Técnico Agrícola' },
];

const validationSchema = Yup.object().shape({
  beneficiaries: Yup.string().required('Campo obrigatório'),
  ataUuid: Yup.string().nullable().required('Campo obrigatório'),
  parceria: Yup.string().required('Campo obrigatório'),
  beneficiariosParceria: Yup.string().when('parceria', {
    is: 'Sim',
    then: (schema) => schema.required('Campo obrigatório'),
    otherwise: (schema) => schema,
  }),
  instituicoes: Yup.array().when('parceria', {
    is: 'Sim',
    then: (schema) => schema.min(1, 'Selecione pelo menos uma instituição'),
    otherwise: (schema) => schema,
  }),
  outraInstituicao: Yup.string().when(['parceria', 'instituicoes'], {
    is: (parceria: string, instituicoes: string[]) =>
      parceria === 'Sim' && instituicoes?.includes('privadas'),
    then: (schema) => schema.required('Campo obrigatório'),
    otherwise: (schema) => schema,
  }),
});

const beneficiaryValidationSchema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
  cpf: Yup.string()
    .required('Preencha esse campo corretamente, com um dado válido.')
    .test('cpf-valid', 'CPF inválido', (value) => validateCPF(value ?? '')),
  telefone: Yup.string()
    .required('Campo obrigatório')
    .matches(/^\(?\d{2}\)?[\s-]?\d{4,5}-?\d{4}$/, 'Telefone inválido'),
  unidadeFamiliar: Yup.string().required('Campo obrigatório'),
  comunidade: Yup.string().required('Campo obrigatório'),
  tipoAtividade: Yup.string().required('Campo obrigatório'),
  atividades: Yup.array().min(1, 'Selecione pelo menos uma atividade'),
  extensionistaNome: Yup.string().required('Campo obrigatório'),
  extensionistaPerfil: Yup.string().required('Campo obrigatório'),
  extensionistaRegistro: Yup.string().required('Campo obrigatório'),
});

const BeneficiaryModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (values: Beneficiary) => void;
  cifa: ICifaForm;
  existingCpfs: string[];
}> = ({ open, onClose, onSave, existingCpfs, cifa }) => {
  const [atividadeInput, setAtividadeInput] = useState('');
  const [atividadesOpcoes, setAtividadesOpcoes] = useState(ATIVIDADES_SEIAF);

  // Extrair handlers para reduzir aninhamento
  const handleAutocompleteChange = (value: any) => {
    let nome: string;
    if (typeof value === 'string') {
      nome = value;
    } else if (value) {
      nome = value.nome;
    } else {
      return;
    }
    setAtividadeInput(nome);
  };

  const handleAddAtividade = (formik: any) => {
    const nome = atividadeInput.trim();
    if (!nome) return;

    let atividade = atividadesOpcoes.find(
      (a) => a.nome.toLowerCase() === nome.toLowerCase(),
    );

    let id: string;
    if (!atividade) {
      id = Math.random().toString(36).substring(2, 9);
      atividade = { id, nome };
      setAtividadesOpcoes((prev) => [
        ...prev,
        atividade as { id: string; nome: string },
      ]);
    } else {
      id = atividade.id;
    }

    if (!formik.values.atividades.includes(id)) {
      formik.setFieldValue('atividades', [
        ...formik.values.atividades,
        id,
      ]);
    }
    setAtividadeInput('');
  };

  const handleRemoveAtividade = (formik: any, id: string) => {
    formik.setFieldValue(
      'atividades',
      formik.values.atividades.filter((a: string) => a !== id),
    );
  };

  const createBeneficiaryPayload = (values: BeneficiaryFormValues): Beneficiary => {
    const atendimentos = [
      {
        cifa_uuid: String(cifa.uuid),
        tipo_atendimento: values.tipoAtividade,
        atividades: values.atividades.map((id: string) => {
          const atividade = atividadesOpcoes.find((a) => a.id === id);
          return atividade ? atividade.nome : id;
        }),
      },
    ];

    return {
      cifa_uuid: String(cifa.uuid),
      nome: values.name,
      cpf: values.cpf.replace(/\D/g, ''),
      contato_telefonico: values.telefone.replace(/\D/g, ''),
      unidade_familiar: values.unidadeFamiliar,
      comunidade_localidade: values.comunidade,
      extensionista: {
        nome: values.extensionistaNome,
        perfil: values.extensionistaPerfil,
        registro: values.extensionistaRegistro,
      },
      atendimentos,
    };
  };

  const formik = useFormik<BeneficiaryFormValues>({
    initialValues: {
      name: '',
      cpf: '',
      telefone: '',
      unidadeFamiliar: '',
      comunidade: '',
      tipoAtividade: '',
      atividades: [],
      extensionistaNome: '',
      extensionistaPerfil: '',
      extensionistaRegistro: '',
    },
    validationSchema: beneficiaryValidationSchema,
    onSubmit: (values, { setFieldError }) => {
      if (existingCpfs.includes(values.cpf)) {
        setFieldError('cpf', 'CPF já cadastrado para este ano');
        return;
      }

      const payload = createBeneficiaryPayload(values);
      BeneficiarioAPI.createBeneficiario(payload);
      onSave(payload);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Adicionar Beneficiário</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <TextField
            label='Nome completo do(a) beneficiário(a)'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            margin='normal'
          />
          <TextField
            label='CPF'
            name='cpf'
            value={formik.values.cpf}
            onChange={(e) =>
              formik.setFieldValue('cpf', formatVisibleCPF(e.target.value))
            }
            onBlur={formik.handleBlur}
            error={formik.touched.cpf && Boolean(formik.errors.cpf)}
            helperText={formik.touched.cpf && formik.errors.cpf}
            fullWidth
            margin='normal'
            inputProps={{ maxLength: 14 }}
            placeholder='000.000.000-00'
          />
          <TextField
            label='Contato (Telefone)'
            name='telefone'
            value={formik.values.telefone}
            onChange={(e) =>
              formik.setFieldValue('telefone', mascaraTelefone(e.target.value))
            }
            onBlur={formik.handleBlur}
            error={formik.touched.telefone && Boolean(formik.errors.telefone)}
            helperText={formik.touched.telefone && formik.errors.telefone}
            fullWidth
            margin='normal'
            placeholder='(99) 99999-9999'
            inputProps={{ maxLength: 15 }}
          />
          <TextField
            label='Unidade Familiar'
            name='unidadeFamiliar'
            value={formik.values.unidadeFamiliar}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.unidadeFamiliar &&
              Boolean(formik.errors.unidadeFamiliar)
            }
            helperText={
              formik.touched.unidadeFamiliar && formik.errors.unidadeFamiliar
            }
            fullWidth
            margin='normal'
          />
          <TextField
            label='Comunidade/Localidade'
            name='comunidade'
            value={formik.values.comunidade}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.comunidade && Boolean(formik.errors.comunidade)
            }
            helperText={formik.touched.comunidade && formik.errors.comunidade}
            fullWidth
            margin='normal'
          />
          <FormControl
            fullWidth
            margin='normal'
            error={
              formik.touched.tipoAtividade &&
              Boolean(formik.errors.tipoAtividade)
            }
          >
            <InputLabel>Tipo de Atividade executada</InputLabel>
            <Select
              name='tipoAtividade'
              value={formik.values.tipoAtividade}
              onChange={formik.handleChange}
              label='Tipo de Atividade executada'
            >
              <MenuItem value='individual'>Atendimento Individual</MenuItem>
              <MenuItem value='grupal'>Atendimento Grupal</MenuItem>
            </Select>
            <FormHelperText>
              {formik.touched.tipoAtividade && formik.errors.tipoAtividade}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin='normal'
            error={
              formik.touched.atividades && Boolean(formik.errors.atividades)
            }
          >
            <Stack direction='row' spacing={1}>
              <Autocomplete
                freeSolo
                options={atividadesOpcoes}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.nome
                }
                inputValue={atividadeInput}
                onInputChange={(_, newInputValue) =>
                  setAtividadeInput(newInputValue)
                }
                onChange={(_, value) => handleAutocompleteChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Adicionar atividade'
                    placeholder='Buscar ou criar atividade...'
                  />
                )}
                sx={{ flex: 1 }}
              />
              <Button
                variant='contained'
                onClick={() => handleAddAtividade(formik)}
                sx={{ height: 40 }}
              >
                Adicionar
              </Button>
            </Stack>
            <FormHelperText>
              {formik.touched.atividades && formik.errors.atividades}
            </FormHelperText>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Atividade</TableCell>
                    <TableCell align='right'>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formik.values.atividades.map((id: string) => {
                    const atividade = atividadesOpcoes.find(
                      (a) => a.id === id,
                    ) || { nome: id };
                    return (
                      <TableRow key={id}>
                        <TableCell>{atividade.nome}</TableCell>
                        <TableCell align='right'>
                          <IconButton
                            color='error'
                            onClick={() => handleRemoveAtividade(formik, id)}
                            size='small'
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {formik.values.atividades.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        align='center'
                        sx={{ color: 'text.secondary' }}
                      >
                        Nenhuma atividade adicionada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </FormControl>
          {/* Sub-módulo Extensionista */}
          <fieldset
            style={{ border: '1px solid #eee', marginTop: 24, padding: 16 }}
          >
            <legend>Extensionista</legend>
            <TextField
              label='Nome completo do Extensionista'
              name='extensionistaNome'
              value={formik.values.extensionistaNome}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.extensionistaNome &&
                Boolean(formik.errors.extensionistaNome)
              }
              helperText={
                formik.touched.extensionistaNome &&
                formik.errors.extensionistaNome
              }
              fullWidth
              margin='normal'
            />
            <FormControl
              fullWidth
              margin='normal'
              error={
                formik.touched.extensionistaPerfil &&
                Boolean(formik.errors.extensionistaPerfil)
              }
            >
              <InputLabel>Perfil de atuação</InputLabel>
              <Select
                name='extensionistaPerfil'
                value={formik.values.extensionistaPerfil}
                onChange={formik.handleChange}
                label='Perfil de atuação'
              >
                {PERFIS.map((perfil) => (
                  <MenuItem key={perfil.id} value={perfil.nome}>
                    {perfil.nome}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formik.touched.extensionistaPerfil &&
                  formik.errors.extensionistaPerfil}
              </FormHelperText>
            </FormControl>
            <TextField
              label='Registro de Classe'
              name='extensionistaRegistro'
              value={formik.values.extensionistaRegistro}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.extensionistaRegistro &&
                Boolean(formik.errors.extensionistaRegistro)
              }
              helperText={
                formik.touched.extensionistaRegistro &&
                formik.errors.extensionistaRegistro
              }
              fullWidth
              margin='normal'
              placeholder='Exemplo: CREA 12345'
            />
          </fieldset>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color='secondary' variant='outlined'>
            Voltar
          </Button>
          <Button type='submit' color='primary' variant='contained'>
            Salvar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Step1: React.FC<Step1Props> = ({ onNext, initialData }) => {
  const [beneficiaryModalOpen, setBeneficiaryModalOpen] = React.useState(false);
  const [beneficiariesList, setBeneficiariesList] = React.useState<
    Beneficiary[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Beneficiary[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { cifa, setPartialForm } = useCifaStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  console.log(initialData);
  const formik = useFormik<Step1FormValues>({
    initialValues: initialData || {
      beneficiaries: '',
      ataUuid: null,
      parceria: '',
      beneficiariosParceria: '',
      docParceriaUuid: null,
      relatorioUuid: null,
      instituicoes: [],
      outraInstituicao: '',
      relatorioUuid_empaer: null,
      relatorioUuid_ensino: null,
      relatorioUuid_ongs: null,
      relatorioUuid_privadas: null,
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = criaPayload();
      CifaAPI.createTechAssist(cifa.uuid ?? '', payload);
      onNext(values);
    },
    enableReinitialize: true,
  });

  // Handlers para DocumentComponent
  const setAtaUuid = (uuid: string) => {
    formik.setFieldValue('ataUuid', uuid);
    setPartialForm('assistencia_tecnica_ata_cmdrs_uuid', uuid);
  };
  const setDocParceriaUuid = (uuid: string) => {
    formik.setFieldValue('docParceriaUuid', uuid);
    setPartialForm('assistencia_tecnica_parceria_empaer_documento_uuid', uuid);
  };

  // Função para buscar no backend (ajuste para sua API)
  const handleSearch = async (term: string) => {
    setIsSearching(true);
    try {
      // Exemplo: ajuste para sua API real
      // const results = await BeneficiarioAPI.searchBeneficiarios(term);
      // setSearchResults(results);
    } catch {
      setSearchResults([]);
    }
    setIsSearching(false);
  };

  // Handlers para reduzir aninhamento
  const handleAddBeneficiaryFromSearch = (beneficiary: Beneficiary) => {
    setBeneficiariesList((prev) => [...prev, beneficiary]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleInstituicaoChange = (instValue: string) => {
    const current = formik.values.instituicoes;
    const newInstituicoes = current.includes(instValue)
      ? current.filter((v) => v !== instValue)
      : [...current, instValue];

    formik.setFieldValue('instituicoes', newInstituicoes);
    updatePartialFormForInstituicao(instValue, newInstituicoes);
  };

  const updatePartialFormForInstituicao = (instValue: string, newInstituicoes: string[]) => {
    const instituicaoMapping = {
      empaer: 'assistencia_tecnica_parceria_empaer',
      ensino: 'assistencia_tecnica_parceria_instituicoes_ensino',
      ongs: 'assistencia_tecnica_parceria_ongs',
      privadas: 'assistencia_tecnica_parceria_outras',
    };

    const formKey = instituicaoMapping[instValue as keyof typeof instituicaoMapping];
    if (formKey) {
      setPartialForm(formKey as keyof ICifaForm, newInstituicoes.includes(instValue));
    }
  };

  const handleRemoveBeneficiary = (index: number) => {
    setBeneficiariesList((prev) => prev.filter((_, i) => i !== index));
  };

  const criaPayload = () => {
    const isEmpaer = formik.values.instituicoes.includes('empaer');
    const isEnsino = formik.values.instituicoes.includes('ensino');
    const isOngs = formik.values.instituicoes.includes('ongs');
    const isPrivadas = formik.values.instituicoes.includes('privadas');

    return {
      assistencia_tecnica_beneficiarios_atendidos:
        Number(formik.values.beneficiaries) || null,
      assistencia_tecnica_ata_cmdrs_uuid: formik.values.ataUuid,
      assistencia_tecnica_parceria_instituicao:
        formik.values.parceria === 'Sim',
      assistencia_tecnica_parceria_beneficiarios_atendidos:
        Number(formik.values.beneficiariosParceria) || null,

      assistencia_tecnica_parceria_empaer: isEmpaer,
      assistencia_tecnica_parceria_empaer_relatorio_documento_uuid: isEmpaer
        ? formik.values.relatorioUuid_empaer
        : null,

      assistencia_tecnica_parceria_instituicoes_ensino: isEnsino,
      assistencia_tecnica_parceria_instituicoes_ensino_relatorio_documento_uuid:
        isEnsino ? formik.values.relatorioUuid_ensino : null,

      assistencia_tecnica_parceria_ongs: isOngs,
      assistencia_tecnica_parceria_ongs_relatorio_documento_uuid: isOngs
        ? formik.values.relatorioUuid_ongs
        : null,

      assistencia_tecnica_parceria_outras: isPrivadas,
      assistencia_tecnica_parceria_outras_nome: isPrivadas
        ? formik.values.outraInstituicao
        : null,
      assistencia_tecnica_parceria_outras_relatorio_documento_uuid: isPrivadas
        ? formik.values.relatorioUuid_privadas
        : null,

      assistencia_tecnica_documento_parceria_uuid:
        formik.values.docParceriaUuid,

      beneficiarios: beneficiariesList,
    };
  };

  const relatorioStoreKey: Record<string, keyof ICifaForm> = {
    empaer: 'assistencia_tecnica_parceria_empaer_relatorio_documento_uuid',
    ensino:
      'assistencia_tecnica_parceria_instituicoes_ensino_relatorio_documento_uuid',
    ongs: 'assistencia_tecnica_parceria_ongs_relatorio_documento_uuid',
    privadas: 'assistencia_tecnica_parceria_outras_relatorio_documento_uuid',
  };

  // Métodos para renderizar estados da busca
  const renderSearchLoading = () => (
    <Typography sx={{ p: 2 }}>Buscando...</Typography>
  );

  const renderSearchEmpty = () => (
    <Typography sx={{ p: 2 }}>
      Nenhum beneficiário encontrado.
    </Typography>
  );

  const renderSearchResults = () => (
    searchResults.map((beneficiary, idx) => (
      <Stack
        key={beneficiary.cpf + idx}
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ p: 2, borderBottom: '1px solid #eee' }}
      >
        <Stack>
          <Typography variant='body1'>
            {beneficiary.nome}
          </Typography>
          <Typography variant='body2' color={Colors.CINZA_MEDIO}>
            CPF: {beneficiary.cpf}
          </Typography>
        </Stack>
        <Button
          variant='outlined'
          size='small'
          onClick={() => {
            handleAddBeneficiaryFromSearch(beneficiary);
          }}
          disabled={beneficiariesList.some(
            (b) => b.cpf === beneficiary.cpf,
          )}
        >
          Adicionar
        </Button>
      </Stack>
    ))
  );

  const renderSearchContent = () => {
    if (isSearching) return renderSearchLoading();
    if (searchResults.length === 0) return renderSearchEmpty();
    return renderSearchResults();
  };

  return (
    <>
      <PageTitle
        title={`Assistência Técnica e Extensão Rural - Município`}
        subtitle='Informe corretamente os dados sobre os atendimentos ATER realizados no Município.'
      />

      <form onSubmit={formik.handleSubmit}>
        <Stack marginX='4rem' spacing='2rem'>
          <Alert
            severity='info'
            sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
          >
            <Typography
              variant='body2'
              fontSize='14px'
              textAlign='left'
              lineHeight={1.5}
            >
              Considera-se Assistência Técnica e Extensão Rural - ATER o número
              de atendimentos prestados aos beneficiários da agricultura
              familiar (unidade agro familiar) do município por profissional de
              ATER, por meio de, no mínimo, 1 (uma) visita presencial ao longo
              do ano.
            </Typography>
          </Alert>
          <ContainerForm title='ATER - Atendidos exclusivamente pelo Município'>
            <TextField
              id='beneficiarios-exclusivamente-municipio'
              label='Quantos beneficiários da Agricultura Familiar (unidade agro familiar) foram atendidos com ATER realizada exclusivamente pelo Município? *'
              variant='outlined'
              name='beneficiaries'
              value={formik.values.beneficiaries}
              onChange={(e) => {
                formik.handleChange(e);
                setPartialForm(
                  'assistencia_tecnica_beneficiarios_atendidos',
                  Number(e.target.value) || null,
                );
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.beneficiaries &&
                Boolean(formik.errors.beneficiaries)
              }
              helperText={
                formik.touched.beneficiaries && formik.errors.beneficiaries
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
                type: 'number',
                min: 0,
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
            />

            <Typography
              sx={{
                color: Colors.CINZA_MEDIO,
                fontWeight: 400,
                marginTop: '1rem',
              }}
            >
              Anexar Ata do CMDRS: *
            </Typography>
            <Typography
              sx={{ color: Colors.CINZA_ESCURO, fontSize: '0.875rem' }}
            >
              Realize abaixo o upload da ata do CMDRS do Município contendo a
              aprovação do Relatório Técnico de atendimentos de ATER praticados
              pelo Município em 2025. (PDF, máximo 20MB)
            </Typography>

            <Box sx={{ width: '100%', marginTop: '1rem' }}>
              <DocumentComponent
                uuid={formik.values.ataUuid}
                setUuid={setAtaUuid}
                podeSubstituir={true}
                podeEnviar={true}
              />
            </Box>
            {formik.touched.ataUuid && formik.errors.ataUuid && (
              <Typography color='error' variant='caption'>
                {formik.errors.ataUuid}
              </Typography>
            )}
          </ContainerForm>

          <ContainerForm title='ATER - Atendidos por Instituições parceiras do Município'>
            <Alert severity='info' sx={{ mb: 2, backgroundColor: '#EAEFF9' }}>
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                De acordo com a Portaria n. 009, de 25 de junho de 2024, a
                mensuração da cobertura de ATER nos municípios levará em
                consideração o esforço empreendido pelo poder público municipal
                em atender os beneficiários da agricultura familiar do município
                com serviços de ATER, não sendo admitida a contabilização de
                atendimentos realizados exclusivamente por entidades executoras
                de ATER sem qualquer colaboração do poder público municipal
                devidamente comprovada.
              </Typography>
            </Alert>
            <FormControl fullWidth>
              <FormLabel id='parceria-label'>
                O Município realizou parceria com uma instituição executora de
                Assistência Técnica e Extensão Rural - ATER em 2025? *
              </FormLabel>
              <RadioGroup
                aria-labelledby='parceria-label'
                name='parceria'
                value={formik.values.parceria}
                onChange={(e) => {
                  formik.handleChange(e);
                  setPartialForm(
                    'assistencia_tecnica_parceria_instituicao',
                    e.target.value === 'Sim',
                  );
                }}
                row
              >
                <FormControlLabel value='Não' control={<Radio />} label='Não' />
                <FormControlLabel value='Sim' control={<Radio />} label='Sim' />
              </RadioGroup>
            </FormControl>
            {formik.touched.parceria && formik.errors.parceria && (
              <Typography color='error' variant='caption'>
                {formik.errors.parceria}
              </Typography>
            )}

            {formik.values.parceria === 'Sim' && (
              <>
                <TextField
                  label='Quantos beneficiários(as) foram atendidos com ATER prestada pela instituição parceira no município? *'
                  variant='outlined'
                  name='beneficiariosParceria'
                  value={formik.values.beneficiariosParceria}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setPartialForm(
                      'assistencia_tecnica_parceria_beneficiarios_atendidos',
                      Number(e.target.value) || null,
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.beneficiariosParceria &&
                    Boolean(formik.errors.beneficiariosParceria)
                  }
                  helperText={
                    formik.touched.beneficiariosParceria &&
                    formik.errors.beneficiariosParceria
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    maxLength: 100,
                    type: 'number',
                    min: 0,
                  }}
                  required
                  sx={{ mt: 2 }}
                  fullWidth
                />

                <Typography sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                  Upload de documento(s) da formalização da parceria entre o
                  município e a(s) instituição(ões) parceira(s) (PDF, opcional)
                </Typography>
                <DocumentComponent
                  uuid={formik.values.docParceriaUuid}
                  setUuid={setDocParceriaUuid}
                  podeSubstituir={true}
                  podeEnviar={true}
                />

                <Typography sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                  Selecione as instituições parceiras:
                </Typography>
                <FormGroup sx={{ mb: 2 }}>
                  {INSTITUICOES_PARCEIRAS.map((inst) => (
                    <FormControlLabel
                      key={inst.value}
                      control={
                        <Checkbox
                          checked={formik.values.instituicoes.includes(
                            inst.value,
                          )}
                          onChange={() => handleInstituicaoChange(inst.value)}
                        />
                      }
                      label={inst.label}
                    />
                  ))}
                </FormGroup>
                {formik.touched.instituicoes && formik.errors.instituicoes && (
                  <Typography color='error' variant='caption'>
                    {formik.errors.instituicoes}
                  </Typography>
                )}
                {formik.values.instituicoes.includes('privadas') && (
                  <TextField
                    label='Informe o nome da instituição privada/outros'
                    name='outraInstituicao'
                    value={formik.values.outraInstituicao}
                    onChange={(e) => {
                      formik.handleChange(e);
                      setPartialForm(
                        'assistencia_tecnica_parceria_outras_nome',
                        e.target.value,
                      );
                    }}
                    onBlur={formik.handleBlur}
                    sx={{ mb: 2 }}
                    fullWidth
                    error={
                      formik.touched.outraInstituicao &&
                      Boolean(formik.errors.outraInstituicao)
                    }
                    helperText={
                      formik.touched.outraInstituicao &&
                      formik.errors.outraInstituicao
                    }
                  />
                )}

                <Typography sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                  Upload do Relatório Técnico Consolidado de cada instituição
                  parceira (PDF)
                </Typography>
                <Stack
                  direction='column'
                  spacing={2}
                  sx={{ mb: 2 }}
                  alignItems='flex-start'
                >
                  {INSTITUICOES_PARCEIRAS.map(
                    (inst) =>
                      formik.values.instituicoes.includes(inst.value) && (
                        <Box key={inst.value} sx={{ width: '100%', mb: 2 }}>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            Relatório Técnico Consolidado ({inst.label})
                          </Typography>
                          <DocumentComponent
                            uuid={
                              formik.values[
                              `relatorioUuid_${inst.value}` as keyof Step1FormValues
                              ] as string | null
                            }
                            setUuid={(uuid) => {
                              formik.setFieldValue(
                                `relatorioUuid_${inst.value}`,
                                uuid,
                              );
                              setPartialForm(
                                relatorioStoreKey[inst.value],
                                uuid,
                              );
                            }}
                            podeSubstituir={true}
                            podeEnviar={true}
                          />
                        </Box>
                      ),
                  )}
                </Stack>
              </>
            )}
          </ContainerForm>

          <ContainerForm title='Lista de Beneficiários - Atendidos Exclusivamente pelo Município'>
            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              marginBottom='1rem'
              sx={{ width: '100%' }}
            >
              <TextField
                label='Buscar beneficiário no sistema'
                variant='outlined'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (e.target.value.length >= 3) {
                    handleSearch(e.target.value);
                  } else {
                    setSearchResults([]);
                  }
                }}
                fullWidth
                sx={{ mb: 0 }}
              />
              <Button
                variant='contained'
                color='primary'
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  height: 56,
                  fontSize: 18,
                  px: 4,
                  minWidth: 240,
                }}
                onClick={() => setBeneficiaryModalOpen(true)}
              >
                + ADICIONAR BENEFICIÁRIO(A)
              </Button>
            </Stack>

            {/* Lista de resultados da busca */}
            {searchTerm.length >= 3 && (
              <Paper sx={{ mb: 2 }}>
                {renderSearchContent()}
              </Paper>
            )}

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Beneficiário</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Atividade Executada</TableCell>
                    <TableCell align='right'>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {beneficiariesList.map((beneficiary, index) =>
                    beneficiary.atendimentos.map((atendimento, idx) => (
                      <TableRow key={`${index}-${idx}`}>
                        {idx === 0 && (
                          <TableCell rowSpan={beneficiary.atendimentos.length}>
                            <Stack>
                              <Typography variant='body1'>
                                {beneficiary.nome}
                              </Typography>
                              <Typography
                                variant='body2'
                                color={Colors.CINZA_MEDIO}
                              >
                                CPF: {beneficiary.cpf}
                              </Typography>
                            </Stack>
                          </TableCell>
                        )}
                        <TableCell>{atendimento.tipo_atendimento}</TableCell>
                        <TableCell>
                          {atendimento.atividades.join(', ')}
                        </TableCell>
                        {idx === 0 && (
                          <TableCell
                            rowSpan={beneficiary.atendimentos.length}
                            align='right'
                          >
                            <Stack
                              direction='row'
                              spacing={1}
                              justifyContent='flex-end'
                            >
                              <IconButton
                                color='primary'
                                size='small'
                                onClick={() => {
                                  setBeneficiaryModalOpen(true);
                                }}
                                aria-label='Editar'
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color='error'
                                size='small'
                                onClick={() => handleRemoveBeneficiary(index)}
                                aria-label='Remover'
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    )),
                  )}
                  {beneficiariesList.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align='center'
                        sx={{ color: 'text.secondary' }}
                      >
                        Nenhum beneficiário adicionado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ContainerForm>

          <Stack
            direction='row'
            spacing={2}
            justifyContent='center'
            alignItems='center'
            marginTop='2rem'
          >
            <Button
              variant='outlined'
              color='primary'
              onClick={() =>
                navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES])
              }
            >
              Voltar
            </Button>

            <Button variant='contained' color='primary' type='submit'>
              Avançar
            </Button>
          </Stack>
          <Stack />
          <BeneficiaryModal
            open={beneficiaryModalOpen}
            onClose={() => setBeneficiaryModalOpen(false)}
            onSave={(beneficiary) => {
              setBeneficiariesList((prev) => [...prev, beneficiary]);
              setBeneficiaryModalOpen(false);
            }}
            cifa={cifa}
            existingCpfs={beneficiariesList.map(
              (beneficiary) => beneficiary.cpf,
            )}
          />
        </Stack>
      </form>
    </>
  );
};

export default Step1;
